import { DEFAULT_COLORS, CUSTOM_COLOR_SLOTS, buildColorCssVars, getColorSwatches } from '../config/global-colors'
import { DEFAULT_FONTS, FONT_OPTIONS, buildFontCssVars, getGoogleFontUrl } from '../config/global-fonts'

const STORAGE_KEY = 'eb-global-styles'

export default function elementorGlobalStyles(editor) {
  const state = loadState()

  function loadState() {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved) return JSON.parse(saved)
    } catch (e) { /* ignore */ }
    return {
      colors: DEFAULT_COLORS.map(c => ({ ...c })),
      customColors: [],
      fonts: DEFAULT_FONTS.map(f => ({ ...f })),
    }
  }

  function saveState() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
    } catch (e) { /* ignore */ }
  }

  function getAllColors() {
    return [...state.colors, ...state.customColors]
  }

  function buildCssVarsBlock() {
    const allColors = getAllColors()
    const colorVars = buildColorCssVars(allColors)
    const fontVars = buildFontCssVars(state.fonts)
    return `:root {\n${colorVars}\n${fontVars}\n}`
  }

  function injectCanvasStyles() {
    const frame = editor.Canvas.getFrameEl()
    if (!frame) return
    const doc = frame.contentDocument
    if (!doc) return

    let styleEl = doc.getElementById('eb-global-styles')
    if (!styleEl) {
      styleEl = doc.createElement('style')
      styleEl.id = 'eb-global-styles'
      doc.head.appendChild(styleEl)
    }
    styleEl.textContent = buildCssVarsBlock()

    const fontUrl = getGoogleFontUrl(state.fonts)
    let linkEl = doc.getElementById('eb-google-fonts')
    if (fontUrl) {
      if (!linkEl) {
        linkEl = doc.createElement('link')
        linkEl.id = 'eb-google-fonts'
        linkEl.rel = 'stylesheet'
        doc.head.appendChild(linkEl)
      }
      linkEl.href = fontUrl
    } else if (linkEl) {
      linkEl.remove()
    }
  }

  function buildSettingsPanel() {
    const container = document.createElement('div')
    container.className = 'eb-global-styles-panel'

    container.innerHTML = `
      <div class="eb-gs-section">
        <div class="eb-gs-section-title">
          <i class="fa fa-palette"></i> Global Colors
        </div>
        <div class="eb-gs-colors"></div>
        <button class="eb-gs-add-color" title="Add custom color">
          <i class="fa fa-plus"></i> Add Color
        </button>
      </div>
      <div class="eb-gs-section">
        <div class="eb-gs-section-title">
          <i class="fa fa-font"></i> Global Fonts
        </div>
        <div class="eb-gs-fonts"></div>
      </div>
      <div class="eb-gs-actions">
        <button class="eb-gs-reset">Reset to Defaults</button>
      </div>
    `

    renderColors(container.querySelector('.eb-gs-colors'))
    renderFonts(container.querySelector('.eb-gs-fonts'))

    container.querySelector('.eb-gs-add-color').addEventListener('click', () => {
      if (state.customColors.length >= CUSTOM_COLOR_SLOTS) return
      const id = `custom-${state.customColors.length + 1}`
      state.customColors.push({ id, label: `Custom ${state.customColors.length + 1}`, value: '#999999' })
      saveState()
      renderColors(container.querySelector('.eb-gs-colors'))
      injectCanvasStyles()
    })

    container.querySelector('.eb-gs-reset').addEventListener('click', () => {
      state.colors = DEFAULT_COLORS.map(c => ({ ...c }))
      state.customColors = []
      state.fonts = DEFAULT_FONTS.map(f => ({ ...f }))
      saveState()
      renderColors(container.querySelector('.eb-gs-colors'))
      renderFonts(container.querySelector('.eb-gs-fonts'))
      injectCanvasStyles()
    })

    return container
  }

  function renderColors(container) {
    container.innerHTML = ''
    const allColors = getAllColors()
    allColors.forEach((color, idx) => {
      const row = document.createElement('div')
      row.className = 'eb-gs-color-row'
      row.innerHTML = `
        <input type="color" class="eb-gs-color-input" value="${color.value}" title="${color.label}" />
        <span class="eb-gs-color-label">${color.label}</span>
        <span class="eb-gs-color-value">${color.value}</span>
        ${idx >= state.colors.length ? '<button class="eb-gs-remove-color" title="Remove"><i class="fa fa-times"></i></button>' : ''}
      `
      const input = row.querySelector('.eb-gs-color-input')
      const valueSpan = row.querySelector('.eb-gs-color-value')
      input.addEventListener('input', (e) => {
        const newVal = e.target.value
        if (idx < state.colors.length) {
          state.colors[idx].value = newVal
        } else {
          state.customColors[idx - state.colors.length].value = newVal
        }
        valueSpan.textContent = newVal
        saveState()
        injectCanvasStyles()
      })
      const removeBtn = row.querySelector('.eb-gs-remove-color')
      if (removeBtn) {
        removeBtn.addEventListener('click', () => {
          state.customColors.splice(idx - state.colors.length, 1)
          saveState()
          renderColors(container)
          injectCanvasStyles()
        })
      }
      container.appendChild(row)
    })
  }

  function renderFonts(container) {
    container.innerHTML = ''
    state.fonts.forEach((font, idx) => {
      const row = document.createElement('div')
      row.className = 'eb-gs-font-row'
      const select = document.createElement('select')
      select.className = 'eb-gs-font-select'
      FONT_OPTIONS.forEach(opt => {
        const option = document.createElement('option')
        option.value = opt.value
        option.textContent = opt.label
        if (opt.value === font.value) option.selected = true
        select.appendChild(option)
      })
      select.addEventListener('change', (e) => {
        const selected = FONT_OPTIONS.find(o => o.value === e.target.value)
        state.fonts[idx].value = e.target.value
        state.fonts[idx].googleFont = selected ? selected.googleFont || '' : ''
        saveState()
        injectCanvasStyles()
      })
      row.innerHTML = `<span class="eb-gs-font-label">${font.label}</span>`
      row.appendChild(select)
      container.appendChild(row)
    })
  }

  editor.on('load', () => {
    injectCanvasStyles()
  })

  editor.Commands.add('open-global-styles', {
    run() {
      const sidebar = document.querySelector('.eb-sidebar-body')
      if (!sidebar) return

      sidebar.querySelectorAll('.eb-panel-view').forEach(v => v.style.display = 'none')

      let panel = sidebar.querySelector('.eb-global-styles-panel')
      if (panel) {
        panel.style.display = 'block'
        return
      }

      const newPanel = buildSettingsPanel()
      newPanel.classList.add('eb-panel-view')
      sidebar.appendChild(newPanel)
      newPanel.style.display = 'block'
    },
    stop() {
      const panel = document.querySelector('.eb-global-styles-panel')
      if (panel) panel.style.display = 'none'
    },
  })

  editor.getGlobalStylesCss = function () {
    return buildCssVarsBlock()
  }

  editor.getGlobalColorSwatches = function () {
    return getColorSwatches(getAllColors())
  }
}
